import React, { useCallback, useState, useId } from 'react';
import classNames from 'classnames';

import { EIcon } from '../../../general-ui/icon/icon';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import TextInput, { Validation } from '../../../general-ui/text-input/text';
import { isValidEmail } from '../../../../utils/utils';
import { useThunkDispatch, useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import { Button } from '../../../general-ui/button/button';
import { OptionalComponent } from '../../../../utils/optional-component';
import { RegFieldsEnum, LanguagesAbbr } from 'types/working-model';
import { requestRegistrationPasswordReset } from 'store/actions/event/event-actions';
import { useLanguageParam } from 'components/live-event/utils';
import { handleRecaptcha } from '../../../../utils/execute-recaptcha';
import { showAlert } from '../../../general-ui/alert/alert-service';
import { FetchError } from '../../../../connection/helpers';
import WaitingIndicator from '@general-ui/waiting-indicator/waiting-indicator';
import RecaptchaV2 from '../../../general-ui/recaptcha-v2/recaptcha-v2';
import ErrorBoundary from 'utils/error-boundary';
import { RequiredFieldsLegend } from '../required-fields-legend';

interface IResetPassword {
	template: string;
	handleExitPasswordResetView?: () => void;
}

export default function ResetPassword({ template, handleExitPasswordResetView }: IResetPassword): JSX.Element {
	const dispatch = useThunkDispatch();
	const language = useLanguageParam() as LanguagesAbbr;
	const { t } = useTranslate('homepage');
	const headingId = useId();
	const eventUuid = useTypedSelector(state => state.LiveEventReducer.eventBundle?.uuid);
	const recaptchaEnabled = useTypedSelector(state => !!state.LiveEventReducer.eventBundle?.registration_settings?.enableRecaptcha);

	const [email, setEmail] = useState('');
	const [valid, setValid] = useState(Validation.normal);
	const [emailSent, setEmailSent] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [recaptchaActionRequired, setRecaptchaActionRequired] = useState(false);
	const [widgetId, setWidgetId] = useState<number | null>(null);

	const getWidgetId = useCallback((id: number) => setWidgetId(id), []);

	const setValue = useCallback((value: string) => {
		setEmail(value);
		setValid(isValidEmail(value) ? Validation.ok : Validation.error);
	}, []);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (isSubmitting) return;

		if (isValidEmail(email) && eventUuid) {
			setIsSubmitting(true);
			try {
				const ACTION = 'password_reset';
				// recaptcha v3, escalating to the v2 challenge on a low score (mirrors sign-in)
				const recaptchaResponse = recaptchaEnabled && await handleRecaptcha({
					action: ACTION,
					useV2: recaptchaActionRequired,
					widgetId,
				});
				const headers = recaptchaResponse?.headers ?? {};
				const result = await dispatch(requestRegistrationPasswordReset(email, language, eventUuid, ACTION, window.location.hostname, headers));

				// v3 scored low → show the "I'm not a robot" challenge; completing it resubmits with a v2 token
				if (result?.actionRequired) {
					setRecaptchaActionRequired(true);
					return;
				}

				// v2 failed server-side (or another flagged error) → route to the catch, keeping the challenge up
				if (result?.error) {
					throw new Error(result.error);
				}

				setRecaptchaActionRequired(false);
				setEmailSent(true);
			} catch (e) {
				console.error(e);
				// 429 = throttled (per-email cooldown / per-IP cap); anything else that reaches here is an
				// unexpected failure (500 / network) — business errors come back as 200 (see the wrapper)
				let message = t('homepage:Something went wrong', 'Something went wrong');
				if (e instanceof FetchError && e.status === 429) {
					message = t('homepage:Too many requests', 'Too many requests');
				}
				showAlert({
					message,
					description: t('homepage:Please try again in a moment', 'Please try again in a moment.'),
					type: 'error',
					duration: 5000,
				});
			} finally {
				setIsSubmitting(false);
			}
		}
	};

	return (
		<div className="registration-slider">
			{/* this component is dynamically rendered so we can hard code "active" class to make sure it's visible when rendered */}
			<div className="registration-panel-v2 fixed active">
				<form className="registration-form" onSubmit={handleSubmit} aria-labelledby={headingId}>
					<div className="registration-form-inner verification" onSubmit={handleSubmit} style={{ overflow: 'hidden' }}>
						<TypographyItem className={classNames("evt-heading-2 stable registration-form-title", template)} tagName="h2" id={headingId}>
							{t("homepage:registration.Reset Password", 'Reset Password')}
						</TypographyItem>
						<RequiredFieldsLegend />
						<br />
						<OptionalComponent display={emailSent}>
							<TypographyItem tagName="p" className={classNames(template)}>
								{t("homepage:registration.Password Reset Request Sent", 'Password Reset Request Sent')} <br />
								{t("homepage:registration.Password Reset Request Sent.subtext", "If an account with the provided email exists, you'll receive instructions shortly to reset your password.")}
							</TypographyItem>
						</OptionalComponent>
						<OptionalComponent display={!emailSent}>
							<div className={classNames('evt-field-wrapper', template)} id={`registration-field-${RegFieldsEnum.email}`}>
								<TextInput
									id="reregister-email-field"
									autoFocus
									placeholder={t(`homepage:registration.registration_fields.${RegFieldsEnum.email}`)}
									label={t(`homepage:registration.registration_fields.${RegFieldsEnum.email}`)}
									required
									onChange={e => setValue(e.target.value)}
									email
									valid={valid}
									value={email}
								/>
								<OptionalComponent display={valid === Validation.error}>
									<p className="registration-error" role="alert">{t("homepage:registration.Please enter a valid email address", 'Please enter a valid email address')}</p>
								</OptionalComponent>
							</div>
						</OptionalComponent>
					</div>
					<ErrorBoundary uniqueLabel='Recaptcha Password Reset'>
						<OptionalComponent display={recaptchaActionRequired}>
							<div style={{ width: '100%', marginTop: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
								<RecaptchaV2 getWidgetId={getWidgetId} />
							</div>
						</OptionalComponent>
					</ErrorBoundary>
					<div className="registration-actions secondary">
						<Button
							isRound={true}
							classButton={classNames('registration-button secondary')}
							template={template}
							onClick={handleExitPasswordResetView}
							typeBtn="button"
							ariaLabel={t('Back', 'Back')}
						>
							<EIcon name="keyboard-arrow-left" />
						</Button>
						<div>
							<Button
								classButton={classNames('registration-button primary position-relative')}
								template={template}
								typeBtn="submit"
								isDisabled={isSubmitting || !email || valid === Validation.error}
							>
								{emailSent
									? t("homepage:registration.Resend", 'Resend')
									: t("homepage:registration.Reset Password", 'Reset Password')}
								{isSubmitting ? <div className='position-absolute'><WaitingIndicator /></div> : null}
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
}
